import { defineResource } from '@tnn/core'
import { z } from 'zod'
import { type ServiceLine, serviceLineSchema, servicesFeeOf } from './service'

/** Lifecycle of a transport job, in order. */
export const shipmentStatuses = ['draft', 'pending', 'assigned', 'picking', 'in_transit', 'delivered', 'cancelled'] as const
export type ShipmentStatus = (typeof shipmentStatuses)[number]

/**
 * How much of a job's `total` its customer paid on receipts of TMS (tms.receipt): `paid` up to the total, else what is
 * left is still owed. Taken from the day it is submitted, before the truck goes too (a deposit is a part paid)
 */
export const shipmentPaymentStatuses = ['unpaid', 'partial', 'paid'] as const
export type ShipmentPaymentStatus = (typeof shipmentPaymentStatuses)[number]

/** Statuses a driver may move a job to from the phone app. */
export const driverStatusFlow: Record<ShipmentStatus, ShipmentStatus[]> = {
  draft: [],
  pending: [],
  assigned: ['picking'],
  picking: ['in_transit'],
  in_transit: ['delivered'],
  delivered: [],
  cancelled: [],
}

export const shipment = defineResource({
  app: 'tms',
  name: 'shipment',
  path: 'shipments',
  schema: z.object({
    customerId: z.string().min(1),
    pickupDate: z.iso.date(),
    pickupTime: z.iso.time({ precision: -1 }).nullish(),
    origin: z.string().trim().min(1).max(200),
    destination: z.string().trim().min(1).max(200),
    distanceKm: z.number().min(0).max(5000).nullish(),
    cargo: z.string().trim().max(200).nullish(),
    pallets: z.number().int().min(0).max(100).nullish(),
    weightKg: z.number().min(0).max(60000).nullish(),
    vehicleId: z.string().nullish(),
    driverId: z.string().nullish(),
    /** Helpers on the truck (tms.driver of kind helper), paid trip pay with the driver */
    helperIds: z.array(z.string()).max(2).default([]),
    eta: z.iso.datetime({ offset: true }).nullish(),
    price: z.number().min(0).nullish(),
    /** Add-on services (`services`), each priced on the pieces or kg it says (`shipmentTotals`) */
    services: z.array(serviceLineSchema).default([]),
    notes: z.string().max(1000).nullish(),
  }),
  fields: {
    number: { readOnly: true, search: true, list: { width: 120, priority: 1 } },
    customerId: {
      type: 'ref',
      ref: { resource: 'accounting.customer', where: { status: 'active', isCustomer: 'true' } },
      filter: true,
      search: true,
      list: { priority: 1 },
      form: { span: 6 },
      import: { aliases: ['ลูกค้า', 'รหัสลูกค้า'] },
    },
    pickupDate: { filter: true, list: { width: 120, priority: 2 }, form: { span: 3 }, import: { aliases: ['วันที่รับ'] }, copy: false },
    pickupTime: { type: 'time', list: { width: 90, priority: 1 }, form: { span: 3 }, import: { aliases: ['เวลารับ'] } },
    origin: { suggest: true, search: true, list: { priority: 1 }, form: { span: 6, section: 'route' }, import: { aliases: ['ต้นทาง'] } },
    destination: {
      suggest: true,
      search: true,
      list: { priority: 1 },
      form: { span: 6, section: 'route' },
      import: { aliases: ['ปลายทาง'] },
    },
    distanceKm: { list: { align: 'end', priority: 3, hidden: true }, form: { span: 4, section: 'route' } },
    cargo: { suggest: true, list: { priority: 3, hidden: true }, form: { span: 4, section: 'cargo' } },
    pallets: { list: { align: 'end', priority: 3, hidden: true }, form: { span: 4, section: 'cargo' } },
    weightKg: { list: { align: 'end', priority: 3, hidden: true }, form: { span: 4, section: 'cargo' } },
    vehicleId: {
      type: 'ref',
      ref: { resource: 'tms.vehicle', where: { status: 'active' } },
      filter: true,
      list: { priority: 2 },
      form: { span: 6, section: 'assign' },
    },
    driverId: {
      type: 'ref',
      ref: { resource: 'tms.driver', where: { status: 'active', kind: 'driver' } },
      filter: true,
      search: true,
      list: { width: 170, priority: 2 },
      form: { span: 6, section: 'assign' },
    },
    helperIds: { type: 'json', list: false, form: { span: 6, section: 'assign', custom: true }, import: false, export: false },
    /** Set on assigning a contract driver: what the trip pays them */
    contractorPay: { readOnly: true, type: 'money', list: { align: 'end', priority: 3, hidden: true } },
    /** The bill for it, made once delivered */
    contractorBillId: { readOnly: true, type: 'ref', ref: { resource: 'accounting.bill' }, list: false },
    /** Set by the server: pending once submitted, then assign and the drivers move it along */
    status: {
      readOnly: true,
      type: 'select',
      options: shipmentStatuses,
      filter: true,
      tones: {
        draft: 'neutral',
        pending: 'warning',
        assigned: 'info',
        picking: 'info',
        in_transit: 'progress',
        delivered: 'neutral',
        cancelled: 'danger',
      },
      list: { width: 150, priority: 1 },
    },
    eta: { list: { width: 90, priority: 2 }, form: { span: 4, section: 'assign' }, copy: false },
    price: { type: 'money', list: { align: 'end', priority: 3, hidden: true }, form: { span: 4, section: 'assign' } },
    services: { type: 'json', list: false, form: { span: 12, section: 'services', custom: true }, import: false },
    servicesFee: { readOnly: true, type: 'money', list: { align: 'end', priority: 3, hidden: true } },
    /** What the job costs in all: its freight (`price`) and its services; what its customer pays on receipts */
    total: { readOnly: true, type: 'money', list: { align: 'end', priority: 3, hidden: true } },
    /** Paid so far on issued receipts (server-managed, `settleJobs`) */
    paid: { readOnly: true, type: 'money', list: { align: 'end', priority: 3, hidden: true } },
    paymentStatus: {
      readOnly: true,
      type: 'select',
      options: shipmentPaymentStatuses,
      filter: true,
      tones: { unpaid: 'warning', partial: 'progress', paid: 'success' },
      list: { width: 120, priority: 2 },
    },
    /** The issued receipts that paid (part of) it */
    receiptIds: { readOnly: true, type: 'multiselect', ref: { resource: 'tms.receipt' }, list: false },
    /** The shipping terms the customer agreed to: those in effect when it was submitted (terms.ts) */
    // The shipping terms' version it keeps, by its number (terms.ts)
    termsId: { readOnly: true, list: false },
    notes: { type: 'textarea', form: { span: 12, section: 'other' } },
    deliveredAt: { readOnly: true, type: 'datetime', list: { hidden: true, priority: 3 } },
    driverUserId: { readOnly: true, list: false, export: false },
  },
  title: 'number',
  columns: [
    'number',
    'customerId',
    'origin',
    'destination',
    'driverId',
    'vehicleId',
    'pickupTime',
    'eta',
    'status',
    'pickupDate',
    'distanceKm',
    'cargo',
    'pallets',
    'weightKg',
    'price',
    'servicesFee',
    'total',
    'paymentStatus',
    'paid',
    'deliveredAt',
  ],
  sections: ['main', 'route', 'cargo', 'assign', 'services', 'other'],
  defaultSort: 'pickupDate,pickupTime',
  actions: ['read', 'create', 'update', 'delete', 'import', 'export', 'print', 'assign'],
  ownerField: 'driverUserId',
  // A draft is a job not yet confirmed; submitted jobs can be assigned. Every field stays editable after submit (logged)
  submittable: true,
  // A regular run: the same customer, route, cargo, truck and price on another day
  duplicate: true,
  card: { title: 'customerId', subtitle: 'destination', meta: ['number', 'pickupTime', 'driverId'], status: 'status' },
})

const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100

/** What a job's services add and its total with the freight (`price`) */
export function shipmentTotals(job: Record<string, unknown>) {
  const servicesFee = servicesFeeOf(job.services as ServiceLine[] | null)
  return { servicesFee, total: round2((Number(job.price) || 0) + servicesFee) }
}

/** What a job's customer still owes: its total less what receipts took */
export const shipmentOwed = (job: Readonly<Record<string, unknown>>) =>
  round2(Math.max((Number(job.total) || 0) - (Number(job.paid) || 0), 0))

/** Paid when nothing is owed and something was paid, `partial` when part was, else unpaid */
export function shipmentPaymentOf(job: Readonly<Record<string, unknown>>): ShipmentPaymentStatus {
  const paid = Number(job.paid) || 0
  if (paid <= 0) return 'unpaid'
  return shipmentOwed(job) > 0 ? 'partial' : 'paid'
}
